import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical } from 'lucide-react';
import { SortableList } from './SortableList';
import { Dropzone } from './Dropzone';
import { ItemActions } from './ItemActions';
import { PreviewModal } from './PreviewModal';

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchEvents = async () => {
    const res = await adminApi.get('/events');
    setEvents(res.data || []);
  };

  useEffect(() => { fetchEvents(); }, []);

  const blank = () => ({
    date: { day: '', month: '', year: '' },
    status: 'upcoming',
    title: '',
    location: '',
    description: '',
    action: 'Register',
    action_url: '',
    image: '',
    order: events.length,
    published: true,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing.id && events.find((x) => x.id === editing.id)) {
        await adminApi.put(`/events/${editing.id}`, editing);
      } else {
        await adminApi.post('/events', { ...editing, id: undefined });
      }
      toast({ title: 'Saved' });
      setEditing(null);
      fetchEvents();
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const ev = events.find((x) => x.id === id);
    const label = ev?.title || 'this event';
    if (!confirm(`Are you sure you want to delete "${label}"? This cannot be undone.`)) return;
    await adminApi.delete(`/events/${id}`);
    toast({ title: 'Deleted' });
    fetchEvents();
  };

  const handleReorder = async (reordered) => {
    setEvents(reordered);
    try { await adminApi.put('/events/reorder', { ids: reordered.map((r) => r.id) }); }
    catch (e) { /* ignore */ }
  };

  const [filter, setFilter] = useState('all'); // all | upcoming | past
  const filteredEvents = events.filter((e) => (filter === 'all' ? true : e.status === filter));

  return (
    <div className="space-y-6" data-testid="events-manager">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold">News & Events</h2>
          <p className="text-xs text-gray-500">Manage upcoming and past events. Filter below.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border bg-white p-1 shadow-sm">
            {[
              { key: 'all', label: `All (${events.length})` },
              { key: 'upcoming', label: `Upcoming (${events.filter(e => e.status === 'upcoming').length})` },
              { key: 'past', label: `Past (${events.filter(e => e.status === 'past').length})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  filter === tab.key ? 'bg-[#1565C0] text-white shadow' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`event-filter-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <Button
            onClick={() => setEditing({ ...blank(), status: filter === 'past' ? 'past' : 'upcoming' })}
            className="bg-[#1565C0]"
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add {filter === 'past' ? 'Past ' : filter === 'upcoming' ? 'Upcoming ' : ''}Event
          </Button>
        </div>
      </div>

      {editing && (
        <Card>
          <CardHeader><CardTitle>{editing.id ? 'Edit Event' : 'New Event'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Day</Label>
                  <Input value={editing.date.day} onChange={(e) => setEditing({ ...editing, date: { ...editing.date, day: e.target.value } })} placeholder="21" />
                </div>
                <div>
                  <Label>Month</Label>
                  <Input value={editing.date.month} onChange={(e) => setEditing({ ...editing, date: { ...editing.date, month: e.target.value } })} placeholder="AUG" />
                </div>
                <div>
                  <Label>Year</Label>
                  <Input value={editing.date.year} onChange={(e) => setEditing({ ...editing, date: { ...editing.date, year: e.target.value } })} placeholder="2026" />
                </div>
              </div>
              <div>
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required data-testid="event-title-input" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                      <SelectItem value="past">Past</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Button Action Text</Label>
                  <Input value={editing.action} onChange={(e) => setEditing({ ...editing, action: e.target.value })} placeholder="Register" />
                </div>
              </div>
              <div>
                <Label>Register / Action URL</Label>
                <Input
                  value={editing.action_url || ''}
                  onChange={(e) => setEditing({ ...editing, action_url: e.target.value })}
                  placeholder="https://forms.gle/... or https://wa.me/60126759467?text=..."
                  data-testid="event-action-url-input"
                />
                <p className="text-xs text-gray-500 mt-1">Where the button should send the user (opens in a new tab).</p>
              </div>
              <div>
                <Label>Event Image (optional) — displays 16:9 on the main page</Label>
                {editing.image && (
                  <div className="mb-2">
                    <div className="w-64 aspect-video rounded border overflow-hidden bg-gray-100">
                      <img
                        src={editing.image?.startsWith('http') ? editing.image : `${process.env.REACT_APP_BACKEND_URL}${editing.image}`}
                        className="w-full h-full object-cover"
                        alt="Preview 16:9"
                        data-testid="event-image-preview"
                      />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Preview at the exact size that will appear on the News page.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-1 text-xs"
                      onClick={() => setEditing({ ...editing, image: '' })}
                    >Remove image</Button>
                  </div>
                )}
                <Dropzone compact onUpload={(url) => setEditing({ ...editing, image: url })} label="Drop event image · recommended 1280 × 720 (16:9)" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editing.published !== false} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                <Label>Published</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid="save-event-btn">{loading ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={filteredEvents}
        onReorder={handleReorder}
        renderItem={(ev) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
              <div className={`w-16 h-16 rounded text-white flex flex-col items-center justify-center flex-shrink-0 ${
                ev.status === 'past'
                  ? 'bg-gradient-to-br from-slate-500 to-slate-700'
                  : 'bg-gradient-to-br from-amber-500 to-orange-500'
              }`}>
                <div className="text-lg font-bold">{ev.date?.day}</div>
                <div className="text-[10px]">{ev.date?.month}</div>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] uppercase font-bold tracking-widest ${
                  ev.status === 'past' ? 'text-slate-500' : 'text-emerald-600'
                }`}>{ev.status}</p>
                <h3 className="font-semibold truncate">{ev.title}</h3>
                <p className="text-sm text-gray-600 truncate">{ev.location}</p>
              </div>
              <ItemActions
                collection="events"
                item={ev}
                onChanged={fetchEvents}
                onEdit={setEditing}
                onDelete={handleDelete}
                onPreview={setPreviewing}
              />
            </CardContent>
          </Card>
        )}
      />
      <PreviewModal kind="event" item={previewing} onClose={() => setPreviewing(null)} />
      {filteredEvents.length === 0 && !editing && (
        <p className="text-center text-gray-500 py-8">
          No {filter === 'all' ? '' : filter + ' '}events yet.
          {filter !== 'all' && ` Click "Add ${filter === 'past' ? 'Past ' : 'Upcoming '}Event" to create one.`}
        </p>
      )}
    </div>
  );
};

export default EventsManager;
